import {
  IDM_POST_OPTIONS,
  DEFAULT_POLLING_INTERVAL,
  IDM_API_URLS,
} from "../constants/constants";
import { getInterventionsAirbrake } from "./getInterventionsAirbrake";

export type IDMResponse = Record<
  string,
  {
    show: boolean;
    component_names?: string[];
    category?: string;
    context_data?: Record<string, unknown>;
  }
>;

type Options = {
  intervalFrequency?: number;
};

const airbrake = getInterventionsAirbrake();

class IDMEmitter {
  private idmApiUrl: string;
  private userId: string;
  private intervalId: number | undefined;
  private intervalFrequency: number;
  private site: string;
  private vertical: string | undefined;
  private eligibilityData: Record<string, unknown>;
  private interventionReceivedCallback: (
    interventionName: string,
    componentNames: string[],
    category: string,
    contextData?: Record<string, unknown>,
  ) => void;

  constructor(
    userId: string,
    site: string,
    vertical: string | undefined,
    interventionReceivedCallback: (
      interventionName: string,
      componentNames: string[],
      category: string,
      contextData?: Record<string, unknown>,
    ) => void,
    eligibilityData?: Record<string, unknown>,
    options?: Options,
  ) {
    this.idmApiUrl = IDM_API_URLS();
    this.userId = userId;
    this.site = site;
    this.vertical = vertical;
    this.intervalId = undefined;
    this.eligibilityData = eligibilityData || {};
    this.intervalFrequency =
      options?.intervalFrequency || DEFAULT_POLLING_INTERVAL;
    this.interventionReceivedCallback = interventionReceivedCallback;
  }

  public start = () => {
    this.intervalId = this.startPolling();
  };

  public startWithoutImmediatePoll = () => {
    this.intervalId = window.setInterval(this.callAPI, this.intervalFrequency);
  };

  public stop = () => {
    this.stopPolling();
  };

  public isPolling = () => {
    return this.intervalId !== undefined;
  };

  public pollNow = () => {
    this.callAPI();
  };

  public setEligibilityData = (data: Record<string, unknown>) => {
    this.eligibilityData = data;
  };

  private startPolling = () => {
    this.callAPI();
    return window.setInterval(this.callAPI, this.intervalFrequency);
  };

  private stopPolling = () => {
    clearInterval(this.intervalId);
    this.intervalId = undefined;
  };

  private callAPI = () => {
    this.fetchInterventions().catch(error => {
      console.error("Error fetching interventions:", error);
    });
  };

  private fetchInterventions = async () => {
    try {
      const response = await fetch(`${this.idmApiUrl}/interventions`, {
        ...IDM_POST_OPTIONS,
        body: JSON.stringify({
          system_user_id: this.userId,
          site: this.site,
          vertical: this.vertical,
          eligibility_data: this.eligibilityData,
        }),
      });

      const data = (await response.json()) as IDMResponse;

      const interventionNames = Object.keys(data);

      for (const interventionName of interventionNames) {
        if (data[interventionName].show === false) continue;

        if (data[interventionName].component_names === undefined) {
          void airbrake.notify({
            error: new Error(
              "No component names provided for intervention with show: true, " +
                interventionName,
            ),
            params: {
              source: "mobius-interventions",
              userId: this.userId,
              site: this.site,
            },
          });

          continue;
        }

        if (data[interventionName].category === undefined) continue;

        this.interventionReceivedCallback(
          interventionName,
          data[interventionName].component_names,
          data[interventionName].category,
          data[interventionName].context_data,
        );
      }
    } catch (error) {
      this.stopPolling();
      console.error("Error fetching interventions from IDM API:", error);
      void airbrake.notify({
        error,
        params: {
          source: "mobius-interventions",
          userId: this.userId,
          site: this.site,
        },
      });
    }
  };
}

export default IDMEmitter;
